import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/api';
import type { RaffleWithDetails, Participant, Winner } from '../types';
import PrizeWheel from '../components/PrizeWheel';

function RaffleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState<RaffleWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [currentWinner, setCurrentWinner] = useState<Participant | null>(null);
  const [pendingWinner, setPendingWinner] = useState<Participant | null>(null);
  const [prizeName, setPrizeName] = useState('');
  const [showWinnerReveal, setShowWinnerReveal] = useState(false);

  // Export participants to CSV
  const handleExportCSV = () => {
    if (!raffle || raffle.participants.length === 0) return;

    const headers = ['First Name', 'Last Name', 'Email'];
    const rows = raffle.participants.map((p: Participant) => [
      p.first_name,
      p.last_name,
      p.email
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${raffle.name.replace(/[^a-z0-9]/gi, '_')}_participants.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export participants to JSON
  const handleExportJSON = () => {
    if (!raffle || raffle.participants.length === 0) return;

    const data = raffle.participants.map((p: Participant) => ({
      first_name: p.first_name,
      last_name: p.last_name,
      email: p.email
    }));

    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${raffle.name.replace(/[^a-z0-9]/gi, '_')}_participants.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Delete raffle
  const handleDelete = async () => {
    if (!id || !raffle) return;
    if (!window.confirm(`Are you sure you want to delete "${raffle.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await adminApi.deleteRaffle(id);
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete raffle');
    }
  };

  const loadRaffle = useCallback(async () => {
    if (!id) return;
    try {
      const data = await adminApi.getRaffle(id);
      setRaffle(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load raffle');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRaffle();
    // Poll for updates every 3 seconds to catch new participants
    const interval = setInterval(loadRaffle, 3000);
    return () => clearInterval(interval);
  }, [loadRaffle]);

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    try {
      await adminApi.updateRaffleStatus(id, status);
      loadRaffle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handlePickWinner = async () => {
    if (!id || !raffle) return;

    // Prevent duplicate spins - check if already spinning
    if (raffle.status === 'spinning' || spinning) {
      setError('A spin is already in progress');
      return;
    }

    // Raffle auto-closes when max winners reached, so just check eligibility
    const eligibleCount = raffle.participants.length - raffle.winners.length;
    if (eligibleCount === 0) {
      setError('No eligible participants remaining');
      return;
    }

    setCurrentWinner(null);
    setPendingWinner(null);
    setShowWinnerReveal(false);
    setError('');

    try {
      // Start spin - picks winner but doesn't record them yet
      // This allows live viewers to see the spin with the winner still in the list
      const result = await adminApi.startSpin(id, prizeName || undefined);
      setPendingWinner(result.winner.participant);
      setSpinning(true); // Now start spinning to the predetermined winner
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start spin');
    }
  };

  const handleSpinComplete = async () => {
    if (!id) return;

    setSpinning(false);

    try {
      // Now confirm the winner after spin animation completes
      await adminApi.confirmWinner(id);

      if (pendingWinner) {
        setCurrentWinner(pendingWinner);
        setShowWinnerReveal(true);
      }

      // Reload to get updated state
      await loadRaffle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm winner');
      loadRaffle();
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    await handleStatusChange('completed');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading raffle...</div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="page">
        <div className="error-message">Raffle not found</div>
        <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
      </div>
    );
  }

  const winnerIds = new Set(raffle.winners.map((w: Winner) => w.participant_id));
  const eligibleParticipants = raffle.participants.filter((p: Participant) => !winnerIds.has(p.id));
  const maxWinnersReached = raffle.winners.length >= raffle.max_winners;
  const winnersRemaining = raffle.max_winners - raffle.winners.length;

  return (
    <div className="page">
      <div className="page-header">
        <Link to="/admin" className="back-link">&larr;</Link>
        <h1>{raffle.name}</h1>
        <span className={`status-badge status-${raffle.status}`}>{raffle.status}</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="detail-grid">
        <div>
          <div className="card">
            <h3>Entry Code</h3>
            <div className="entry-code">{raffle.entry_code}</div>
            <p className="text-muted" style={{ marginTop: '0.5rem' }}>
              Share this code with participants to join
            </p>
            {raffle.status !== 'active' && raffle.status !== 'spinning' && (
              <div className="card-actions">
                <button className="btn btn-danger" onClick={handleDelete}>
                  Delete Raffle
                </button>
              </div>
            )}
          </div>

          {raffle.winners.length > 0 && (
            <div className="card">
              <h3 className="accent-warning">Winners ({raffle.winners.length} / {raffle.max_winners})</h3>
              <div className="participants-grid">
                {raffle.winners.map((w: Winner) => (
                  <div key={w.id} className="participant-chip winner">
                    {w.first_name} {w.last_name}
                    {w.prize_name && ` - ${w.prize_name}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <h3>Participants ({raffle.participants.length})</h3>
            {raffle.participants.length === 0 ? (
              <p className="text-muted">No participants yet</p>
            ) : (
              <>
                <div className="export-buttons" style={{ marginTop: 0, marginBottom: '1rem' }}>
                  <button className="btn btn-secondary" onClick={handleExportCSV}>
                    Download CSV
                  </button>
                  <button className="btn btn-secondary" onClick={handleExportJSON}>
                    Download JSON
                  </button>
                </div>
                <div className="participants-grid">
                  {raffle.participants.map((p: Participant) => (
                    <div
                      key={p.id}
                      className={`participant-chip ${winnerIds.has(p.id) ? 'winner' : ''}`}
                    >
                      {p.first_name} {p.last_name}
                      {winnerIds.has(p.id) && ' (Winner)'}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          {raffle.status === 'pending' && (
            <div className="card">
              <h3>Get Started</h3>
              <p>Open registration to allow participants to join using the entry code.</p>
              <button
                className="btn btn-action"
                onClick={() => handleStatusChange('active')}
                style={{ marginTop: '1rem' }}
              >
                Open Registration
              </button>
            </div>
          )}

          {(raffle.status === 'active' || raffle.status === 'spinning') && (
            <>
              <div className="card">
                <div className="card-header">
                  <h3 style={{ margin: 0 }}>Spin the Wheel</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => navigate(`/admin/raffle/${id}/present`)}
                    >
                      Present
                    </button>
                    <button
                      className="btn btn-warning btn-small"
                      onClick={handleComplete}
                    >
                      End Raffle
                    </button>
                    <button
                      className="btn btn-danger btn-small"
                      onClick={handleDelete}
                    >
                      Delete Raffle
                    </button>
                  </div>
                </div>
                <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                  Winners: {raffle.winners.length} / {raffle.max_winners}
                </p>
                {maxWinnersReached ? (
                  <div className="success-message">
                    All {raffle.max_winners} winner{raffle.max_winners !== 1 ? 's have' : ' has'} been selected!
                  </div>
                ) : eligibleParticipants.length === 0 ? (
                  <p className="text-muted">
                    {raffle.participants.length === 0
                      ? 'Waiting for participants to join...'
                      : 'No more eligible participants remaining.'}
                  </p>
                ) : (
                  <>
                    <p className="text-muted">
                      {eligibleParticipants.length} eligible participant{eligibleParticipants.length !== 1 ? 's' : ''} remaining
                      {winnersRemaining > 0 && ` | ${winnersRemaining} winner${winnersRemaining !== 1 ? 's' : ''} left to pick`}
                    </p>
                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label>Prize Name (optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={prizeName}
                        onChange={e => setPrizeName(e.target.value)}
                        placeholder="e.g., Grand Prize, Gift Card"
                      />
                    </div>
                    <button
                      className="btn btn-action"
                      onClick={handlePickWinner}
                      disabled={spinning || raffle.status === 'spinning' || eligibleParticipants.length === 0 || maxWinnersReached}
                      style={{ marginTop: '1rem' }}
                    >
                      {spinning || raffle.status === 'spinning' ? 'Spinning...' : 'Spin to Pick Winner'}
                    </button>
                  </>
                )}
              </div>

              {/* Bug fix: Hide wheel when max winners reached, show winners list instead */}
              {maxWinnersReached ? (
                <div className="winners-display">
                  <div className="winner-announcement">
                    <h2>All Winners Selected!</h2>
                    <div className="winners-list">
                      {raffle.winners.map((w: Winner, index: number) => (
                        <div key={w.id} className="winner-item">
                          <span className="winner-number">#{index + 1}</span>
                          <span className="winner-name">{w.first_name} {w.last_name}</span>
                          {w.prize_name && <span className="winner-prize">{w.prize_name}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {showWinnerReveal && currentWinner ? (
                    <div className="winner-reveal-container">
                      <div className="winner-reveal">
                        <div className="winner-reveal-icon">🎉</div>
                        <h2>We Have a Winner!</h2>
                        <div className="winner-reveal-name">
                          {currentWinner.first_name} {currentWinner.last_name}
                        </div>
                        {prizeName && (
                          <div className="winner-reveal-prize">Won: {prizeName}</div>
                        )}
                        <button
                          className="btn btn-action"
                          onClick={() => {
                            setShowWinnerReveal(false);
                            setCurrentWinner(null);
                            setPendingWinner(null);
                            setPrizeName('');
                          }}
                          style={{ marginTop: '1.5rem' }}
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={`wheel-container ${spinning ? 'wheel-spinning' : ''}`}>
                      <PrizeWheel
                        participants={eligibleParticipants}
                        spinning={spinning}
                        winner={pendingWinner}
                        onSpinComplete={handleSpinComplete}
                      />
                    </div>
                  )}
                </>
              )}

            </>
          )}

          {raffle.status === 'completed' && (
            <div className="card">
              <h3 className="accent-success">Raffle Complete</h3>
              <p>This raffle has ended. {raffle.winners.length} winner{raffle.winners.length !== 1 ? 's were' : ' was'} selected.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RaffleDetailPage;
